import { FC, useEffect, useRef, useState } from 'react';

import { OnboardingArrow } from './onboarding-arrow';
import { OnboardingTourProps } from './types';

/**
 * OnboardingTour component displays a sequential onboarding tour with arrows
 * pointing to specific UI elements. Each arrow fades in one at a time,
 * with configurable delay between steps. All previous arrows remain visible.
 *
 * @example
 * ```tsx
 * const steps = [
 *   { id: '1', text: 'Start here', selector: '#start-button', direction: 'bottom-left' },
 *   { id: '2', text: 'Then click here', selector: '.next-step', direction: 'top-right' },
 *   { id: '3', text: 'Finally, review this', selector: '[data-tour="review"]', direction: 'bottom-right' },
 * ];
 *
 * <OnboardingTour
 *   steps={steps}
 *   isActive={showTour}
 *   stepDelayMs={250}
 * />
 * ```
 */
export const OnboardingTour: FC<OnboardingTourProps> = ({ steps, stepDelayMs = 250, isActive, zIndex = 1100 }) => {
	// Track which step IDs are visible (by ID, not index, to survive dismissals)
	const [visibleStepIds, setVisibleStepIds] = useState<Set<string>>(new Set());
	// Track if initial animation has completed to avoid re-animating on dismissals
	const hasInitializedRef = useRef(false);

	// Reset when tour becomes inactive
	useEffect(() => {
		if (!isActive) {
			setVisibleStepIds(new Set());
			hasInitializedRef.current = false;
		}
	}, [isActive]);

	// Sequential step reveal logic - only runs once on initial activation
	// Dependencies are intentionally [isActive, stepDelayMs]. We read steps from a ref so that
	// when activeSteps gets a new reference (e.g. after dismissals load), we don't re-run this
	// effect and clear the timers before they fire.
	const stepsRef = useRef(steps);
	const timersRef = useRef<NodeJS.Timeout[]>([]);
	stepsRef.current = steps;

	useEffect(() => {
		if (!isActive || stepsRef.current.length === 0 || hasInitializedRef.current) {
			return;
		}

		hasInitializedRef.current = true;
		const currentSteps = stepsRef.current;

		// Show first step immediately
		setVisibleStepIds(new Set([currentSteps[0].id]));

		// Schedule subsequent steps
		const timers: NodeJS.Timeout[] = [];
		currentSteps.forEach((step, index) => {
			if (index === 0) return;

			const timer = setTimeout(() => {
				setVisibleStepIds((prev) => new Set([...prev, step.id]));
			}, stepDelayMs * index);
			timers.push(timer);
		});
		timersRef.current = timers;

		return () => {
			timersRef.current.forEach((t) => clearTimeout(t));
			timersRef.current = [];
		};
	}, [isActive, stepDelayMs]);

	// Don't render anything if tour is not active
	if (!isActive || steps.length === 0) {
		return null;
	}

	return (
		<>
			{steps.map((step, index) => (
				<OnboardingArrow key={step.id} step={step} isVisible={visibleStepIds.has(step.id)} zIndex={zIndex + index} />
			))}
		</>
	);
};

OnboardingTour.displayName = 'OnboardingTour';
